import * as ApiRequests from '../../api/requests';
import { AuthHelper } from '../../types/auth-helper-types';
import { AppThunk } from '../actions';
import { SierraDeviceActions } from '../actions/sierra-device-actions';
import { ChildDeviceRegistrationData, GatewayDeviceRegistration, Uart } from '../../types/device';
import { getExistingChildDevicesUnitId } from '../../lib/device-helpers';

const createChildDevice = async (
    dispatch: any,
    deviceRegistration: ChildDeviceRegistrationData,
    addToast: Function,
    authHelper: AuthHelper
): Promise<void> => {
    const payload = await ApiRequests.createSWChildDevice(deviceRegistration, { authHelper });
    if (payload.ok) {
        dispatch(SierraDeviceActions.sierraChildDeviceRegistrationSucceeded(payload.response.message));
    } else {
        addToast(`${ApiRequests.extractResponseNotOkMessage(payload.response)}`, 
            'error'
        );
        dispatch(SierraDeviceActions.sierraChildDeviceRegistrationFailed(payload.httpStatusCode.toString()));
    }
};
const updateChildDevice = async (
    pxWhiteDeviceId: string,
    siteId: string,
    deviceName: string,
    pxWhiteParentDeviceId: string,
    ipAddress: string,
    unitId: number,
    ipPort: number,
    dispatch: any,
    addToast: Function,
    authHelper: AuthHelper
): Promise<void> => {
    const payload = await ApiRequests.updateChildDeviceDetails(
        {
            pxWhiteChildDeviceId: pxWhiteDeviceId,
            parentPxWhiteGatewayId: pxWhiteParentDeviceId,
            childIPAddress: ipAddress,
            childUnitId: unitId,
            siteId: siteId,
            childDeviceName: deviceName,
            validateInput: true,
            ipPort: ipPort,
        },
        { authHelper }
    );
    if (payload.ok) {
        dispatch(SierraDeviceActions.sierraChildDeviceUpdateSucceeded(payload.response.message));
    } else {
        addToast(`${ApiRequests.extractResponseNotOkMessage(payload.response)}`, 'error');
        dispatch(SierraDeviceActions.sierraChildDeviceUpdateFailed(payload.httpStatusCode.toString()));
    }
};
const extheremUnitIdValidation = (
    allowDuplicateUnitId: number,
    unitIdchildList: any[],
    dispatch: any,
    unitId: number
): boolean => {
    let continueCreation = true;
    const unitIdList = unitIdchildList.reduce((obj, b) => {
        obj[b] = ++obj[b] || 1;
        return obj;
    }, {});

    for (const [key, value] of Object.entries(unitIdList)) {
        if (key === unitId.toString() && (value as number) >= allowDuplicateUnitId) {
            dispatch(SierraDeviceActions.exerthermUnitIdValidation(true));
            continueCreation = false;
            break;
        }
    }
    return continueCreation;
};

export const SierraDeviceThunks = {
    GatewayDeviceRegistration: (
        deviceRegistration: GatewayDeviceRegistration,
        deviceValue: string[] | undefined,
        authHelper: AuthHelper,
        addToast: Function
    ): AppThunk => async (dispatch): Promise<void> => {
        dispatch(SierraDeviceActions.sierraGatewayDeviceRegistrationStarted());

        const payload = await ApiRequests.createSWGatewayDevice(deviceRegistration, { authHelper });

        if (payload.ok) {
            dispatch(
                SierraDeviceActions.sierraGatewayDeviceRegistrationSucceeded(
                    payload.response.message,
                    deviceValue || []
                )
            );
        } else {
            const errorCode = ApiRequests.extractResponseNotOkCode(payload.response);
            const errorMessage = ApiRequests.extractErrorCodeWithMessage(payload.response);

            if (!(errorCode === 9021 && errorMessage.includes('imei')))
                addToast(`${ApiRequests.extractResponseNotOkMessage(payload.response)}`,  'error');
            dispatch(SierraDeviceActions.sierraGatewayDeviceRegistrationFailed(errorCode.toString()));
        }
    },

    ChildDeviceRegistration: (
        deviceRegistration: ChildDeviceRegistrationData,
        authHelper: AuthHelper,
        addToast: Function
    ): AppThunk => async (dispatch): Promise<void> => {
        dispatch(SierraDeviceActions.sierraChildDeviceRegistrationStarted());

        await createChildDevice(dispatch, deviceRegistration, addToast, authHelper);
    },

    ChildDeviceRegistrationWithUnitIdValidation: (
        gatewayDeviceId: string,
        siteId: string,
        allowDuplicateUnitId: number,
        deviceRegistration: ChildDeviceRegistrationData,
        authHelper: AuthHelper,
        addToast: Function
    ): AppThunk => async (dispatch): Promise<void> => {
        dispatch(SierraDeviceActions.sierraChildDeviceRegistrationStarted());

        const gatewayDetailsPayload = await ApiRequests.getSierraGatewayDeviceDetails(
            {
                pxWhiteGatewayDeviceId: gatewayDeviceId,
                siteId: siteId,
            },
            { authHelper }
        );

        if (gatewayDetailsPayload.ok) {
            const unitIdchildList = getExistingChildDevicesUnitId(
                gatewayDetailsPayload.response.data.configTreeJson,
                deviceRegistration.modelType
            );
            if (unitIdchildList && unitIdchildList.length > 0) {
                if (allowDuplicateUnitId > 0) {
                    const continueCreation = extheremUnitIdValidation(
                        allowDuplicateUnitId,
                        unitIdchildList,
                        dispatch,
                        deviceRegistration.unitId
                    );
                    if (continueCreation) {
                        await createChildDevice(dispatch, deviceRegistration, addToast, authHelper);
                    }
                } else {
                    await createChildDevice(dispatch, deviceRegistration, addToast, authHelper);
                }
            } else await createChildDevice(dispatch, deviceRegistration, addToast, authHelper);
        } else {
            addToast(`${ApiRequests.extractResponseNotOkMessage(gatewayDetailsPayload.response)}`,  'error');
            dispatch(
                SierraDeviceActions.sierraChildDeviceRegistrationFailed(gatewayDetailsPayload.httpStatusCode.toString())
            );
        }
    },

    UpdateGatewayDevice: (
        pxWhiteDeviceId: string,
        octaveGatewayId: string,
        siteId: string,
        childDevicesTypes: string,
        ipAddress: string,
        uart: Uart,
        netmask: number,
        organizationName: string,
        authHelper: AuthHelper,
        addToast: Function
    ): AppThunk => async (dispatch): Promise<void> => {
        dispatch(SierraDeviceActions.sierraGatewayDeviceRegistrationStarted());

        const payload = await ApiRequests.updateGatewayDeviceDetails(
            {
                pxWhiteDeviceId: pxWhiteDeviceId,
                octaveId: octaveGatewayId,
                siteId: siteId,
                childDeviceTypes: childDevicesTypes.toLowerCase(),
                ipAddress: ipAddress,
                uart: uart,
                netmask: netmask,
                vendorName: organizationName,
            },
            { authHelper }
        );
        if (payload.ok) {
            dispatch(SierraDeviceActions.sierraGatewayDeviceUpdateSucceeded(payload.response.message));
        } else {
            addToast(`${ApiRequests.extractResponseNotOkMessage(payload.response)}`, 'error');
            dispatch(SierraDeviceActions.sierraGatewayDeviceUpdateFailed(payload.httpStatusCode.toString()));
        }
    },

    DeleteDevice: (
        deviceIds: string[],
        authHelper: AuthHelper,
        addToast: Function,
        adopterId?: string,
    ): AppThunk => async (dispatch): Promise<void> => {
       dispatch(SierraDeviceActions.sierraGatewayDeviceRegistrationStarted());
      
        const payload = await ApiRequests.deleteDevice(
            {
                deviceIds: deviceIds,
                adopterId: adopterId,
            },
            { authHelper }
        );
        if (payload.ok) {
            dispatch(SierraDeviceActions.deviceDeleteSucceeded(payload.response.message));
        } else {
            addToast(`${ApiRequests.extractResponseNotOkMessage(payload.response)}`, 'error');
            dispatch(SierraDeviceActions.deviceDeleteFailed(payload.httpStatusCode.toString()));
        }
    },

    UpdateChildDevice: (
        pxWhiteDeviceId: string,
        siteId: string,
        deviceName: string,
        pxWhiteParentDeviceId: string,
        ipAddress: string,
        unitId: number,
        ipPort: number,
        authHelper: AuthHelper,
        addToast: Function
    ): AppThunk => async (dispatch): Promise<void> => {
        dispatch(SierraDeviceActions.sierraChildDeviceRegistrationStarted());

        await updateChildDevice(
            pxWhiteDeviceId,
            siteId,
            deviceName,
            pxWhiteParentDeviceId,
            ipAddress,
            unitId,
            ipPort,
            dispatch,
            addToast,
            authHelper
        );
    },
    UpdateChildDeviceWithUnitIdValidation: (
        pxWhiteDeviceId: string,
        siteId: string,
        deviceName: string,
        pxWhiteParentDeviceId: string,
        ipAddress: string,
        unitId: number,
        model: string,
        ipPort: number,
        authHelper: AuthHelper,
        addToast: Function,
        allowDuplicateUnitId: number
    ): AppThunk => async (dispatch): Promise<void> => {
        dispatch(SierraDeviceActions.sierraChildDeviceUpdateStarted());
        const gatewayDetailsPayload = await ApiRequests.getSierraGatewayDeviceDetails(
            {
                pxWhiteGatewayDeviceId: pxWhiteParentDeviceId,
                siteId: siteId,
            },
            { authHelper }
        );

        if (gatewayDetailsPayload.ok) {
            const unitIdchildList = getExistingChildDevicesUnitId(
                gatewayDetailsPayload.response.data.configTreeJson || {},
                model
            );
            if (unitIdchildList && unitIdchildList.length > 0) {
                if (allowDuplicateUnitId > 0) {
                    const continueCreation = extheremUnitIdValidation(
                        allowDuplicateUnitId,
                        unitIdchildList,
                        dispatch,
                        unitId
                    );
                    if (continueCreation)
                        await updateChildDevice(
                            pxWhiteDeviceId,
                            siteId,
                            deviceName,
                            pxWhiteParentDeviceId,
                            ipAddress,
                            unitId,
                            ipPort,
                            dispatch,
                            addToast,
                            authHelper
                        );
                } else {
                    await updateChildDevice(
                        pxWhiteDeviceId,
                        siteId,
                        deviceName,
                        pxWhiteParentDeviceId,
                        ipAddress,
                        unitId,
                        ipPort,
                        dispatch,
                        addToast,
                        authHelper
                    );
                }
            }
        } else {
            addToast(`${ApiRequests.extractResponseNotOkMessage(gatewayDetailsPayload.response)}`, 'error');
            dispatch(
                SierraDeviceActions.sierraChildDeviceRegistrationFailed(gatewayDetailsPayload.httpStatusCode.toString())
            );
        }
    },

    deleteChildDevice: (deviceId: string, addToast: Function, authHelper: AuthHelper): AppThunk => async (
        dispatch
    ): Promise<void> => {
        dispatch(SierraDeviceActions.sierraChildDeviceDeleteStarted());

        const payload = await ApiRequests.deleteChildDevice(
            {
                deviceIds: [deviceId],
            },
            { authHelper }
        );

        if (payload.ok) {
            dispatch(SierraDeviceActions.sierraChildDeviceDeleteSucceeded(payload.response.message));
            return;
        }
        addToast(`${ApiRequests.extractResponseNotOkMessage(payload.response)}`, 'error');
        dispatch(
            SierraDeviceActions.sierraChildDeviceDeleteFailed(
                `${ApiRequests.extractResponseNotOkCode(payload.response)}`
            )
        );
    },
};
